import {
  Box,
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import ResourceState from "../../../../components/Resource/ResourceState";
import {
  CUSTOMERS_DOCUMENTS_STATES,
  CUSTOMERS_DOCUMENTS_STATES_VALUE_NOT_SENT,
} from "../../../../constants/customersDocuments";
import useInterpolateTranslatedField from "../../../../hooks/useInterpolateTranslatedField";
import CustomersDocumentsPagesActionCreate from "../../../CustomersDocumentsPages/Actions/Create";
import DocumentsActionDownload from "../../../Documents/Actions/Download";
import CustomersDocumentsActionAccept from "../../Actions/Accept";
import CustomersDocumentsActionCreate from "../../Actions/Create";
import CustomersDocumentsActionDownload from "../../Actions/Download";
import CustomersDocumentsActionRefuse from "../../Actions/Refuse";
import Page from "./Page";

/**
 * @typedef {Object} Props
 * @property {number} userId
 * @property {"customer" | "holder"} userType
 * @property {import("../../../../types/Document").Document} document
 * @property {import("../../../../types/CustomerDocument").CustomerDocument} [customerDocument]
 * @property {() => void} mutateCustomerDocument
 * @property {boolean} isOpen
 * @property {() => void} onClose
 *
 * @param {Props} props
 */
function PagesModal({
  userId,
  userType,
  document,
  customerDocument,
  mutateCustomerDocument,
  isOpen,
  onClose: _onClose,
}) {
  /** @type {import("react").MutableRefObject<HTMLButtonElement | null>} */
  const ref = useRef(null);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex((currentIndex) => {
      const pagesLength = customerDocument?.pages?.length ?? 0;
      if (currentIndex >= pagesLength) {
        return Math.max(pagesLength - 1, 0);
      }
      return currentIndex;
    });
  }, [customerDocument?.pages?.length, index]);

  // on réinitialise le tab focused quand le modal se ferme
  const onClose = () => {
    setIndex(0);
    _onClose();
  };

  const translate = useInterpolateTranslatedField();

  return (
    <Modal
      initialFocusRef={ref}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="full"
      isCentered>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          <Wrap spacing="1rem">
            <ResourceState
              state={
                customerDocument?.state ??
                CUSTOMERS_DOCUMENTS_STATES_VALUE_NOT_SENT
              }
              customStates={CUSTOMERS_DOCUMENTS_STATES}
            />

            <Text>{translate(document.name)}</Text>
          </Wrap>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody display="flex" py="0" flexDirection="column">
          {customerDocument?.review && (
            <Text colorScheme="red" fontSize="0.75rem">
              <FormattedMessage
                defaultMessage="Motif de refus: {reason}"
                values={{ reason: customerDocument.review }}
              />
            </Text>
          )}

          {customerDocument?.pages && customerDocument.pages.length > 0 ? (
            <Tabs
              index={index}
              isLazy={true}
              lazyBehavior="keepMounted"
              display="flex"
              flexDirection="column"
              variant="solid-rounded"
              colorScheme="brandPrimary"
              flexGrow={1}>
              {customerDocument?.pages && customerDocument.pages.length > 0 && (
                <HStack justify="center">
                  <Box overflowX="auto">
                    <TabList>
                      {customerDocument.pages.map((page, index) => (
                        <Tab
                          onClick={(event) => {
                            event.currentTarget.scrollIntoView({
                              behavior: "smooth",
                              inline: "center",
                            });
                            setIndex(index);
                          }}
                          whiteSpace="nowrap"
                          key={page.id}>
                          {page.filename}
                        </Tab>
                      ))}
                    </TabList>
                  </Box>
                </HStack>
              )}

              <TabPanels display="flex" flexDirection="column" flexGrow={1}>
                {customerDocument?.pages?.map((page) => (
                  <TabPanel
                    display="flex"
                    flexGrow={1}
                    p="0"
                    mt="1rem"
                    key={page.id}>
                    <Page
                      page={page}
                      mutateCustomerDocument={mutateCustomerDocument}
                    />
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          ) : (
            <Center flexGrow={1} h="full">
              <VStack spacing="1rem">
                <Text fontSize="larger" textAlign="center">
                  {translate(document?.name)}
                </Text>

                {translate(document.infos) && (
                  <Text textAlign="center">{translate(document.infos)}</Text>
                )}

                <Text colorScheme="gray" textAlign="center">
                  <FormattedMessage defaultMessage="Aucun fichier n'a été envoyé pour le moment" />
                </Text>

                <DocumentsActionDownload
                  document={document}
                  render={({ isAllowed, trigger, isLoading }) =>
                    isAllowed ? (
                      <Button
                        onClick={trigger}
                        isLoading={isLoading}
                        colorScheme="gray">
                        <FormattedMessage defaultMessage="Télécharger le modèle" />
                      </Button>
                    ) : null
                  }
                />
              </VStack>
            </Center>
          )}
        </ModalBody>

        <ModalFooter>
          <Wrap justify="flex-end">
            <Button onClick={onClose} colorScheme="gray">
              <FormattedMessage defaultMessage="Retour" />
            </Button>

            <CustomersDocumentsActionDownload
              customerDocument={customerDocument}
              render={({ isAllowed, trigger, isLoading }) =>
                isAllowed ? (
                  <Button
                    onClick={trigger}
                    isLoading={isLoading}
                    colorScheme="gray">
                    <FormattedMessage defaultMessage="Télécharger tous les fichiers" />
                  </Button>
                ) : null
              }
            />

            <CustomersDocumentsActionCreate
              userType={userType}
              userId={userId}
              documentId={document.id}
              mutateCustomerDocument={mutateCustomerDocument}
              customerDocument={customerDocument}
              render={({ isAllowed, trigger }) =>
                isAllowed ? (
                  <Button onClick={trigger} colorScheme="brandPrimary">
                    <FormattedMessage defaultMessage="Téléverser" />
                  </Button>
                ) : null
              }
            />

            {customerDocument && (
              <CustomersDocumentsPagesActionCreate
                documentId={customerDocument.id}
                mutateCustomerDocument={mutateCustomerDocument}
                customerDocument={customerDocument}
                render={({ isAllowed, trigger }) =>
                  isAllowed ? (
                    <Button onClick={trigger} colorScheme="brandPrimary">
                      <FormattedMessage defaultMessage="Téléverser" />
                    </Button>
                  ) : null
                }
              />
            )}

            <CustomersDocumentsActionRefuse
              customerDocument={customerDocument}
              mutate={mutateCustomerDocument}
              render={({ isAllowed, trigger }) =>
                isAllowed ? (
                  <Button onClick={trigger} colorScheme="red">
                    <FormattedMessage defaultMessage="Refuser" />
                  </Button>
                ) : null
              }
            />

            <CustomersDocumentsActionAccept
              customerDocument={customerDocument}
              mutate={mutateCustomerDocument}
              render={({ isAllowed, trigger }) =>
                isAllowed ? (
                  <Button onClick={trigger} colorScheme="green">
                    <FormattedMessage defaultMessage="Accepter" />
                  </Button>
                ) : null
              }
            />
          </Wrap>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default PagesModal;
